/**
 * Создание задачи (§11.3, §11.4). Макет data/Задачник.dc.html, префикс cf*.
 *
 * Открывается «+» в заголовке столбца — столбец тогда уже выбран — или
 * кнопкой «Создать задачу» в шапке доски. Доска и подразделение не
 * выбираются: задача наследует их от текущей доски, проект — от доски
 * или столбца (§2.4).
 *
 * Прежде создать задачу в интерфейсе было нельзя вовсе: ни кнопки, ни формы,
 * ни вызова tasks.create.
 */

import { Permission, ProjectBindingMode, StatusSource } from "@taskflow/contracts";
import { useEffect, useRef, useState } from "react";

import { useAppSelector } from "@/app/hooks";
import {
  errorMessage,
  isEmptyDoc,
  MAX_DESCRIPTION_CHARS,
  newRequestId,
  plainText,
  type RichDoc,
  usePermission,
} from "@taskflow/client-core";
import { uploadSigned } from "@/shared/api/http";
import { todayIso } from "@/shared/lib/date";
import {
  Button,
  DateInput,
  FormField,
  IconButton,
  Input,
  Modal,
  MultiSelectPicker,
  type PickerOption,
  RichTextEditor,
  Select,
  toast,
} from "@/shared/ui";
import { Trash2, Upload } from "@/shared/ui/motionIcons";

import { usePrioritiesQuery, useTaskTypesQuery } from "@taskflow/client-core/admin";
import {
  type BoardDetail,
  useBoardMembersQuery,
  useBoardTagsQuery,
} from "@taskflow/client-core/boards";
import { useDepartmentTabs } from "@taskflow/client-core/boards";
import {
  useCompleteAttachmentMutation,
  useCreateTaskMutation,
  useRequestAttachmentUploadMutation,
} from "@taskflow/client-core/tasks";
import { ALLOWED_TYPES, attachmentMime, attachmentProblem } from "./attachmentRules";
import { LocalFilePreview } from "./LocalFilePreview";
import { RecordingControl } from "./recording/RecordingControl";
import { canRecordScreen, isRecordedVideo } from "./recording/useScreenRecorder";
import { plannedStatusId, systemStatusOptions } from "@taskflow/client-core/boards";

/** Файл, ждущий создания задачи, и ссылка на его превью (для картинок). */
interface PendingFile {
  file: File;
  previewUrl: string | null;
}

/** Object URL для картинки и видео или null — для прочих файлов и там, где
 *  object URL не поддерживается (jsdom). Освобождает вызывающий. */
function previewUrlOf(file: File): string | null {
  const viewable = file.type.startsWith("image/") || file.type.startsWith("video/");
  if (!viewable || typeof URL.createObjectURL !== "function") return null;
  return URL.createObjectURL(file);
}

function sizeLabel(bytes: number): string {
  if (bytes < 1024 * 1024) return `${String(Math.max(1, Math.round(bytes / 1024)))} КБ`;
  return `${(bytes / 1024 / 1024).toFixed(1).replace(".", ",")} МБ`;
}

export function CreateTaskDialog({
  board,
  initialColumnId,
  onClose,
}: {
  board: BoardDetail;
  /** Столбец, чей «+» нажат; null — форма открыта из шапки доски. */
  initialColumnId: string | null;
  onClose: () => void;
}) {
  const user = useAppSelector((state) => state.auth.user);
  const members = useBoardMembersQuery(board.id);
  const departmentTabs = useDepartmentTabs();
  const boardTags = useBoardTagsQuery(board.id);
  const priorities = usePrioritiesQuery({});
  const taskTypes = useTaskTypesQuery({});
  const [createTask] = useCreateTaskMutation();
  const [requestUpload] = useRequestAttachmentUploadMutation();
  const [completeUpload] = useCompleteAttachmentMutation();

  const canAssign = usePermission(Permission.TASK_ASSIGN, board.permissions);
  const canTag = usePermission(Permission.TAG_ASSIGN, board.permissions);
  const canAttach = usePermission(Permission.FILE_ADD, board.permissions);

  const columnMode = board.projectBindingMode === ProjectBindingMode.COLUMN;
  // Запись бага — только на досках «один проект на всю доску».
  const canRecord =
    canAttach && board.projectBindingMode === ProjectBindingMode.BOARD && canRecordScreen();
  const columns = board.columns.filter((column) => column.isActive);
  const fallbackColumn =
    columns.find((column) => column.id === board.createColumnId) ?? columns[0];

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState<RichDoc | null>(null);
  const [columnId, setColumnId] = useState<string | null>(
    initialColumnId ?? fallbackColumn?.id ?? null,
  );
  const [typeId, setTypeId] = useState<string | null>(null);
  const [priorityId, setPriorityId] = useState<string | null>(null);
  const [dueDate, setDueDate] = useState("");
  const [statusTagId, setStatusTagId] = useState<string | null>(null);
  // §11.3: постановщик по умолчанию — текущий пользователь.
  const [reporterId, setReporterId] = useState<string | null>(user?.id ?? null);
  const [assigneeIds, setAssigneeIds] = useState<string[]>([]);
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [files, setFiles] = useState<PendingFile[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Идёт запись экрана: форму не закрыть и не отправить, иначе запись пропадёт.
  const [recording, setRecording] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  // Ссылки превью, ещё не освобождённые: при закрытии формы — все сразу,
  // иначе байты вставленных картинок висели бы в памяти до перезагрузки.
  const previewUrls = useRef(new Set<string>());

  useEffect(() => {
    const urls = previewUrls.current;
    return () => {
      for (const url of urls) URL.revokeObjectURL(url);
      urls.clear();
    };
  }, []);

  // Архивные значения справочников новой задаче не предлагаются (§7.1).
  // Порядок — по position справочника, а не по имени, как отдаёт сервер:
  // иначе «средний» приоритет по алфавиту оказывался «Низким».
  const typeOptions: PickerOption[] = (taskTypes.data?.data ?? [])
    .filter((item) => item.status === "ACTIVE")
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .map((item) => ({ id: item.id, label: item.name }));
  const priorityOptions: PickerOption[] = (priorities.data?.data ?? [])
    .filter((item) => item.status === "ACTIVE")
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .map((item) => ({ id: item.id, label: item.name }));

  // Умолчания — когда справочники загрузились: первый тип и средний по
  // порядку приоритет, как «Обычный» в макете. Подстройка во время рендера,
  // а не в эффекте: форма не мигает пустыми полями.
  if (typeId === null && typeOptions.length > 0) setTypeId(typeOptions[0]?.id ?? null);
  if (priorityId === null && priorityOptions.length > 0) {
    setPriorityId(priorityOptions[Math.floor((priorityOptions.length - 1) / 2)]?.id ?? null);
  }

  const memberOptions: PickerOption[] = (members.data?.data ?? []).map((member) => ({
    id: member.userId,
    label: `${member.lastName} ${member.firstName}`,
    ...(member.position === null ? {} : { hint: member.position }),
  }));
  const selfId = user?.id ?? null;
  const selfIsMember = selfId !== null && memberOptions.some((option) => option.id === selfId);
  const selfAssigned = selfId !== null && assigneeIds.includes(selfId);
  // Только активные обычные теги этой доски: статусный тег ставит сервер (§10.2).
  const tagOptions: PickerOption[] = (boardTags.data?.data ?? [])
    .filter((tag) => tag.kind === "REGULAR" && tag.status === "ACTIVE")
    .map((tag) => ({ id: tag.id, label: tag.name }));
  // На доске «проект на столбце» статус несёт системный тег (§10.2) и
  // выбирается вручную; по умолчанию — «Запланировано».
  const statusByTag = board.statusSource === StatusSource.SYSTEM_TAG;
  const statusOptions = statusByTag ? systemStatusOptions(boardTags.data?.data) : [];
  if (statusByTag && statusTagId === null && statusOptions.length > 0) {
    setStatusTagId(plannedStatusId(boardTags.data?.data) ?? statusOptions[0]?.id ?? null);
  }
  const columnOptions: PickerOption[] = columns.map((column) => ({
    id: column.id,
    label: column.name,
    ...(columnMode && column.projectName !== null ? { hint: column.projectName } : {}),
  }));

  const selectedColumn = columns.find((column) => column.id === columnId);
  const projectLabel = columnMode
    ? (selectedColumn?.projectName ?? "не задан")
    : (board.projectShortName ?? board.projectName ?? "не задан");

  const pickFiles = (list: ArrayLike<File> | null) => {
    const accepted: PendingFile[] = [];
    for (const file of Array.from(list ?? [])) {
      const problem = attachmentProblem(file);
      if (problem !== null) {
        toast.warning(problem, file.name);
        continue;
      }
      const previewUrl = previewUrlOf(file);
      if (previewUrl !== null) previewUrls.current.add(previewUrl);
      accepted.push({ file, previewUrl });
    }
    setFiles((prev) => [...prev, ...accepted]);
    if (fileInput.current) fileInput.current.value = "";
  };

  const removeFile = (index: number) => {
    const previewUrl = files[index]?.previewUrl ?? null;
    if (previewUrl !== null) {
      URL.revokeObjectURL(previewUrl);
      previewUrls.current.delete(previewUrl);
    }
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const validate = (): string | null => {
    if (title.trim() === "") return "Укажите название задачи";
    if (selectedColumn === undefined) return "Выберите столбец";
    if (typeId === null) return "Выберите тип задачи";
    if (priorityId === null) return "Выберите приоритет";
    if (description !== null && plainText(description).length > MAX_DESCRIPTION_CHARS) {
      return `Описание не длиннее ${String(MAX_DESCRIPTION_CHARS)} символов`;
    }
    if (dueDate !== "" && dueDate < todayIso()) return "Срок не может быть раньше сегодняшнего дня";
    // §12.2: то же правило проверит сервер, но сказать о нём лучше до отправки.
    if (selectedColumn.requireAssigneeOnEntry && assigneeIds.length === 0) {
      return `Столбец «${selectedColumn.name}» требует исполнителя — выберите хотя бы одного`;
    }
    return null;
  };

  const submit = async () => {
    const problem = validate();
    if (problem !== null || selectedColumn === undefined || typeId === null || priorityId === null) {
      setError(problem ?? "Заполните обязательные поля");
      return;
    }
    setError(null);
    setSaving(true);

    const task = await createTask({
      boardId: board.id,
      columnId: selectedColumn.id,
      title: title.trim(),
      descriptionDoc: description === null || isEmptyDoc(description) ? null : description,
      typeId,
      priorityId,
      dueDate: dueDate === "" ? null : dueDate,
      assigneeIds,
      tagIds,
      reporterId,
      ...(statusByTag && statusTagId !== null ? { statusTagId } : {}),
      clientRequestId: newRequestId(),
    })
      .unwrap()
      .catch((raw: unknown) => {
        setError(errorMessage(raw));
        return null;
      });
    if (task === null) {
      setSaving(false);
      return;
    }

    // Файлы — после создания: подписанная загрузка требует номер задачи (§14.2).
    // Неудача с файлом не отменяет задачу — файл приложат в карточке.
    const failed: string[] = [];
    for (const { file } of files) {
      try {
        const ticket = await requestUpload({
          key: task.key,
          filename: file.name,
          mime: attachmentMime(file),
          size: file.size,
          ...(isRecordedVideo(file) ? { recording: true } : {}),
        }).unwrap();
        await uploadSigned(ticket.uploadUrl, file);
        await completeUpload({ id: ticket.attachmentId }).unwrap();
      } catch {
        failed.push(file.name);
      }
    }

    setSaving(false);
    toast.success("Задача создана", `${task.key} · ${task.title}`);
    if (failed.length > 0) {
      toast.warning("Не все файлы приложены", `${failed.join(", ")} — добавьте их в карточке задачи`);
    }
    onClose();
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Создать задачу"
      width="lg"
      dismissable={!saving && !recording}
      footer={
        <>
          {error !== null && (
            <p role="alert" className="mr-auto self-center text-[13px] font-semibold text-danger">
              {error}
            </p>
          )}
          <Button variant="secondary" onClick={onClose} disabled={saving || recording}>
            Отмена
          </Button>
          <Button
            loading={saving}
            disabled={recording}
            title={recording ? "Сначала остановите запись" : undefined}
            onClick={() => void submit()}
          >
            Создать
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3.5">
        <FormField label="Название" required>
          {(props) => (
            <Input
              {...props}
              value={title}
              autoFocus
              onChange={(event) => setTitle(event.target.value)}
            />
          )}
        </FormField>

        <FormField label="Описание">
          {() => (
            <RichTextEditor
              value={null}
              onChange={setDescription}
              ariaLabel="Описание задачи"
              maxChars={MAX_DESCRIPTION_CHARS}
              // Вставленный в описание файл (снимок экрана) идёт в список
              // файлов задачи, а не в текст.
              {...(canAttach ? { onPasteFiles: pickFiles } : {})}
            />
          )}
        </FormField>

        <div className="grid gap-3.5 sm:grid-cols-2">
          <Inherited
            label="Подразделение"
            value={departmentTabs.labels[board.departmentCode] ?? board.departmentCode}
            note="определяется доской"
          />
          <Inherited
            label="Проект"
            value={projectLabel}
            note={columnMode ? "определяется столбцом" : "определяется доской"}
          />

          <FormField label="Столбец" required>
            {(props) => (
              <Select
                {...props}
                label="Столбец задачи"
                placeholder="Выберите столбец"
                options={columnOptions}
                value={columnId}
                onChange={setColumnId}
              />
            )}
          </FormField>

          {statusByTag && (
            <FormField label="Статус">
              {(props) => (
                <Select
                  {...props}
                  label="Статус задачи"
                  placeholder={boardTags.isLoading ? "Загрузка…" : "Запланировано"}
                  options={statusOptions}
                  value={statusTagId}
                  onChange={setStatusTagId}
                />
              )}
            </FormField>
          )}

          <FormField label="Срок">
            {(props) => (
              <DateInput {...props} min={todayIso()} value={dueDate} onChange={setDueDate} />
            )}
          </FormField>

          <FormField label="Тип задачи" required>
            {(props) => (
              <Select
                {...props}
                label="Тип задачи"
                placeholder={taskTypes.isLoading ? "Загрузка…" : "Выберите тип"}
                options={typeOptions}
                value={typeId}
                onChange={setTypeId}
              />
            )}
          </FormField>

          <FormField label="Приоритет" required>
            {(props) => (
              <Select
                {...props}
                label="Приоритет задачи"
                placeholder={priorities.isLoading ? "Загрузка…" : "Выберите приоритет"}
                options={priorityOptions}
                value={priorityId}
                onChange={setPriorityId}
              />
            )}
          </FormField>

          <FormField
            label="Постановщик"
            {...(canAssign ? {} : { hint: "Изменить можно с правом назначать исполнителей" })}
          >
            {(props) => (
              <Select
                {...props}
                label="Постановщик"
                placeholder="Не указан"
                options={memberOptions}
                value={reporterId}
                onChange={setReporterId}
                disabled={!canAssign}
              />
            )}
          </FormField>

          <FormField label="Исполнители">
            {(props) => (
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <MultiSelectPicker
                    id={props.id}
                    label="Исполнители задачи"
                    placeholder="Не назначены"
                    options={memberOptions}
                    value={assigneeIds}
                    onChange={setAssigneeIds}
                    emptyText={members.isLoading ? "Загрузка участников…" : "Нет участников доски"}
                  />
                </div>
                {/* «Я» — назначить себя одним нажатием, не листая участников.
                    Только участнику доски: иначе сервер отклонил бы исполнителя. */}
                <Button
                  variant="secondary"
                  title="Назначить себя исполнителем"
                  aria-label="Назначить себя исполнителем"
                  disabled={!selfIsMember || selfAssigned}
                  onClick={() => selfId !== null && setAssigneeIds([...assigneeIds, selfId])}
                >
                  Я
                </Button>
              </div>
            )}
          </FormField>
        </div>

        {canTag && (
          <FormField label="Теги">
            {(props) => (
              <MultiSelectPicker
                id={props.id}
                label="Теги задачи"
                placeholder="Без тегов"
                options={tagOptions}
                value={tagIds}
                onChange={setTagIds}
                emptyText={boardTags.isLoading ? "Загрузка тегов…" : "На доске нет тегов"}
              />
            )}
          </FormField>
        )}

        {canAttach && (
          <div className="flex flex-col gap-1.5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-[13px] font-semibold text-muted">Файлы</span>
              {canRecord && (
                <RecordingControl
                  disabled={saving}
                  onRecorded={pickFiles}
                  onActiveChange={setRecording}
                />
              )}
            </div>
            <div
              className="flex flex-wrap items-center justify-center gap-2 rounded-md border border-dashed border-line px-4 py-3 text-xs text-faint"
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => {
                event.preventDefault();
                pickFiles(event.dataTransfer.files);
              }}
            >
              Перетащите файлы сюда или
              <Button
                variant="secondary"
                size="sm"
                icon={Upload}
                disabled={saving}
                onClick={() => fileInput.current?.click()}
              >
                Выберите файлы
              </Button>
              <input
                ref={fileInput}
                type="file"
                multiple
                hidden
                accept={ALLOWED_TYPES.join(",")}
                aria-label="Файлы задачи"
                onChange={(event) => pickFiles(event.target.files)}
              />
            </div>
            {files.length > 0 && (
              <ul className="flex flex-col gap-1.5">
                {files.map(({ file, previewUrl }, index) => (
                  <li
                    key={previewUrl ?? `${file.name}-${String(index)}`}
                    className="flex items-center gap-2 rounded-md border border-line px-2.5 py-1.5 text-xs"
                  >
                    <LocalFilePreview
                      name={file.name}
                      url={previewUrl}
                      video={file.type.startsWith("video/")}
                    />
                    <span className="min-w-0 flex-1 truncate text-fg">{file.name}</span>
                    <span className="shrink-0 text-faint">{sizeLabel(file.size)}</span>
                    <IconButton
                      icon={Trash2}
                      label={`Убрать файл ${file.name}`}
                      size="sm"
                      variant="danger"
                      disabled={saving}
                      onClick={() => removeFile(index)}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

/** Значение, которое задача наследует и которое в форме не меняется. */
function Inherited({ label, value, note }: { label: string; value: string; note: string }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[13px] font-semibold text-muted">{label}</span>
      <p className="flex h-9 items-center gap-1.5 truncate text-sm text-fg">
        {value}
        <span className="text-xs text-faint">({note})</span>
      </p>
    </div>
  );
}
